import * as PIXI from "pixi.js";
import {Application} from "../Application";
import {Anchor} from "../core/Anchor";
import {texture} from "../core/AssetHelpers";
import {BitmapText} from "../core/BitmapText";
import {Container} from "../core/Container";
import {Callback, list} from "../core/Reflection";
import {Poly, Rect} from "../core/Shape";
import {Sprite} from "../core/Sprite";
import {Assets} from "../data/Assets";
import {IBookData} from "../data/BooksData";
import {IChooseStoryData} from "../data/IChooseStoryData";
import {ColorRampFilter} from "../filters/ColorRampFilter";
import {Button} from "./Button";

export class BookAnimation extends Container {

    public readonly animationContainer = this.addChild(new Container());
    public readonly firstPage = this.addChild(new Container());
    public readonly lastPage = this.addChild(new Container());

    public readonly lastPageLeft = this.lastPage.addChild(new Container());
    public readonly lastPageRight = this.lastPage.addChild(new Container());
    public readonly theEnd = this.lastPageLeft.addChild(new Sprite());
    public readonly lastScene = this.lastPageLeft.addChild(new LastScene());
    public readonly storyPictureLast = this.lastPageRight.addChild(new Sprite());
    public readonly storyTitleLast = this.lastPageRight.addChild(new Sprite());

    public readonly firstPageLeft = this.firstPage.addChild(new Container());
    public readonly firstPageRight = this.firstPage.addChild(new Container());
    public readonly bookAnimationFrame = list(() => this.animationContainer.addChild(new Sprite()));
    public readonly pageMask: number[] = [];
    public readonly storyPicture = this.firstPageRight.addChild(new Sprite());
    public readonly storyTitle = this.firstPageRight.addChild(new Sprite());
    public readonly buttonNext = this.firstPageRight.addChild(new Button());
    public readonly storyText = this.firstPageLeft.addChild(new BitmapText("", {
        font: Assets.fontGrandstander.assetName,
        align: "center",
        lineHeight: 1.2,
        tint: 0x000000,
    }, Anchor.middleCenter));
    public readonly buttonSpeaker = this.firstPageLeft.addChild(new Button());
    public readonly bookCovers = list(() => this.addChild(new Sprite()));
    public readonly buttonHome = this.addChild(new Button());
    private _pageMask!: Poly;

    private _currentFrame = 0;
    private _frameTime = 0;
    private _forward = false;
    private _callback?: Callback;

    public onLayout() {
        this._pageMask = new Poly(this.pageMask);
    }

    public openBook(bookData: IBookData, delay?: number | IChooseStoryData, cb?: Callback) {
        if (typeof delay === "object" && delay.storyFinished) {
            this.lastPage.visible = true;
            this.firstPage.visible = false;
            this.storyPictureLast.setTexturePreservingTransform(`book-cover-${bookData.bookId}`);
            this.storyTitleLast.setTexturePreservingTransform(`book-story-title-${bookData.bookId}`);
            if (delay.sceneRender) {
                this.lastScene.sceneRender.texture = delay.sceneRender;
                this.lastScene.sceneRender.scale.set(Math.max(
                    this.lastScene.sceneMask.width / delay.sceneRender.width,
                    this.lastScene.sceneMask.height / delay.sceneRender.height));
            }
        } else {
            this.lastPage.visible = false;
            this.firstPage.visible = true;
            this.storyText.text = bookData.storyText;
            this.storyPicture.setTexturePreservingTransform(`book-story-picture-${bookData.bookId}`);
            this.storyTitle.setTexturePreservingTransform(`book-story-title-${bookData.bookId}`);
        }

        const filter = new ColorRampFilter(texture(`color-ramp-book-${bookData.bookId}`)!);
        filter.padding = -2;
        for (const frame of this.bookAnimationFrame) {
            frame.filters = [filter];
        }

        for (const cover of this.bookCovers) {
            cover.setTexturePreservingTransform(`book-cover-${bookData.bookId}`);
        }

        this.firstPageLeft.visible = false;
        this.firstPageRight.visible = false;
        this.lastPageRight.visible = false;
        this.lastPageRight.visible = false;

        this._currentFrame = 0;
        this._frameTime = 0;
        this._forward = true;
        this._callback = () => {
            this.firstPageLeft.visible = true;
            if (cb) {
                cb();
            }
        };
        this.hideFrames();

        if (typeof delay === "number") {
            this.displayFrame(this._currentFrame);
            this.delayedCall(delay, () => {
                Application.instance.ticker.add(this.update, this);
            });
        } else if (typeof delay === "object") {
            this.firstPageLeft.visible = true;
            this.firstPageRight.visible = true;
            this.lastPageRight.visible = true;
            this.lastPageRight.visible = true;
            this._currentFrame = this.bookAnimationFrame.length - 1;
            this.displayFrame(this._currentFrame);
        } else {
            Application.instance.ticker.add(this.update, this);
        }
    }

    public closeBook(cb?: Callback) {
        this.firstPageLeft.visible = false;
        this.lastPageLeft.visible = false;
        this.buttonHome.visible = false;

        this._currentFrame = this.bookAnimationFrame.length - 1;
        this._frameTime = 0;
        this._forward = false;
        this._callback = cb;
        this.hideFrames();
        this.displayFrame(this._currentFrame);
        Application.instance.ticker.add(this.update, this);
    }

    public destroy(): void {
        this._callback = undefined;
        Application.instance.ticker.remove(this.update, this);
        super.destroy();
    }

    private update(time: number) {
        const frameInterval = 1 / 20;
        this._frameTime += 0.001 * time / PIXI.settings.TARGET_FPMS;
        if (this._frameTime >= frameInterval) {
            this._frameTime -= frameInterval;
            if (this._forward && this._currentFrame < this.bookAnimationFrame.length - 1) {
                this.displayFrame(this._currentFrame + 1);
            } else if (!this._forward && this._currentFrame > 0) {
                this.displayFrame(this._currentFrame - 1);
            } else {
                Application.instance.ticker.remove(this.update, this);
                if (this._callback) {
                    const cb = this._callback;
                    this._callback = undefined;
                    cb();
                }
            }
        }
    }

    private hideFrames() {
        for (const frame of this.bookAnimationFrame) {
            frame.visible = false;
        }
        for (const cover of this.bookCovers) {
            cover.visible = false;
        }
    }

    private displayFrame(index: number) {
        this.bookAnimationFrame[this._currentFrame].visible = false;
        if (this._currentFrame < this.bookCovers.length) {
            this.bookCovers[this._currentFrame].visible = false;
        }
        this._currentFrame = index;
        this.bookAnimationFrame[index].visible = true;
        if (index < this.bookCovers.length) {
            this.bookCovers[index].visible = true;
        }
        this.firstPageRight.visible = index > 2;
        this.lastPageRight.visible = index > 2;
        if (index === 3) {
            this.addChild(this._pageMask);
            this.firstPageRight.mask = this._pageMask;
            this.lastPageRight.mask = this._pageMask;
        } else {
            this._pageMask.removeFromParent();
            this.firstPageRight.mask = null;
            this.lastPageRight.mask = null;
        }
    }
}

class LastScene extends Container {

    public readonly sceneMask = this.addChild(new Rect(100, 100));
    public readonly sceneRender = this.addChild(new Sprite(undefined, Anchor.middleCenter));
    public readonly sceneFrame = this.addChild(new Sprite());

    public onLayout() {
        this.sceneRender.mask = this.sceneMask;
        this.sceneRender.x = this.sceneMask.x + this.sceneMask.width * 0.5;
        this.sceneRender.y = this.sceneMask.y + this.sceneMask.height * 0.5;
    }
}
